import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { products } from '../mockData';
import { useInquiry } from '../context/InquiryContext'; 
import { ChevronLeft, ChevronRight, CheckCircle, MessageCircle, ZoomIn, ZoomOut, ShoppingBag, X, Mail } from 'lucide-react';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = products.find((p) => p.id === parseInt(id));

  const { addToInquiry } = useInquiry();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomActive, setIsZoomActive] = useState(false);
  const [backgroundPos, setBackgroundPos] = useState('50% 50%');
  const [addedPopup, setAddedPopup] = useState(false);
  const [showContactOptions, setShowContactOptions] = useState(false);
  const imageRef = useRef(null);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return process.env.PUBLIC_URL + imagePath;
  };

  const imageArray = product?.images?.length > 0 
    ? product.images 
    : (product?.image ? [product.image] : []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentIndex(0); 
    setIsZoomActive(false);
  }, [product]);

  const nextImage = () => {
    setIsZoomActive(false);
    setCurrentIndex((prev) => (prev === imageArray.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    setIsZoomActive(false);
    setCurrentIndex((prev) => (prev === 0 ? imageArray.length - 1 : prev - 1));
  };

  const handleThumbnailClick = (index) => {
    setIsZoomActive(false);
    setCurrentIndex(index);
  };

  const handleMouseMove = (e) => {
    if (!isZoomActive || !imageRef.current) return;
    const { left, top, width, height } = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setBackgroundPos(`${x}% ${y}%`);
  };

  const handleInquiryCartAdd = () => {
    if (product) {
      addToInquiry(product, 1, '');
      setAddedPopup(true);
      setTimeout(() => setAddedPopup(false), 3000);
    }
  };

  const handleWhatsAppInquiry = (phone) => {
    const textMessage = `Hello AARTEMA, I would like to request a custom quotation and layout specifications for the following catalog item:\n\n• Product: ${product.name}\n• Category: ${product.subCategory || product.category}\n\nPlease share finish samples and bulk configuration options.`;
    const encodedMessage = encodeURIComponent(textMessage);
    window.open(`https://wa.me/91${phone}?text=${encodedMessage}`, '_blank');
    setShowContactOptions(false);
  };

  const handleEmailInquiry = () => {
    const subject = encodeURIComponent(`Inquiry for ${product.name}`);
    const body = encodeURIComponent(`Hello AARTEMA,\n\nI would like to request a custom quotation and layout specifications for the following catalog item:\n\n• Product: ${product.name}\n• Category: ${product.subCategory || product.category}\n\nPlease share finish samples and bulk configuration options.\n\nThank you.`);
    window.location.href = `mailto:trm.iitd@gmail.com,aravmangla20@gmail.com?subject=${subject}&body=${body}`;
    setShowContactOptions(false);
  };

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold mb-4">Product Not Found</h2>
        <Link to="/products" className="text-[#C1A57B] hover:underline">Return to Collection</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        
        <div className="flex flex-row justify-between items-center mb-12">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors group w-max"
          >
            <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" /> 
            Back to Collection
          </button>

          <Link 
            to="/inquiry-cart" 
            className="flex items-center gap-2 px-5 py-2.5 bg-gray-50 rounded-xl hover:bg-gray-100 transition text-sm font-semibold border border-gray-100"
          >
            <ShoppingBag size={16} /> View Inquiry Sheet
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          
          <div className="space-y-6">
            <div 
              ref={imageRef}
              className={`relative w-full aspect-square md:aspect-[4/3] lg:aspect-[4/5] bg-[#F7F7F7] rounded-3xl overflow-hidden shadow-sm group ${isZoomActive ? 'cursor-crosshair' : 'cursor-default'}`}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setBackgroundPos('50% 50%')}
            >
              <div 
                className="w-full h-full transition-all duration-200 ease-out"
                style={{
                  backgroundImage: `url(${getImageUrl(imageArray[currentIndex])})`,
                  backgroundPosition: isZoomActive ? backgroundPos : 'center',
                  backgroundSize: isZoomActive ? '250%' : 'contain', 
                  backgroundRepeat: 'no-repeat',
                }}
              />
              
              <button 
                onClick={(e) => { e.stopPropagation(); setIsZoomActive(!isZoomActive); setBackgroundPos('50% 50%'); }}
                className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full text-black text-xs font-bold tracking-wide uppercase shadow-sm border border-gray-100 hover:bg-white hover:shadow-md transition-all flex items-center gap-2 z-10"
              >
                {isZoomActive ? <ZoomOut size={14} /> : <ZoomIn size={14} />}
                {isZoomActive ? 'Close Zoom' : 'Inspect'}
              </button>

              {imageArray.length > 1 && !isZoomActive && (
                <>
                  <button 
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white p-3 rounded-full shadow-sm text-black transition-all opacity-0 group-hover:opacity-100 border border-gray-100"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white p-3 rounded-full shadow-sm text-black transition-all opacity-0 group-hover:opacity-100 border border-gray-100"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
            
            {imageArray.length > 1 && (
              <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar justify-center">
                {imageArray.map((imgUrl, index) => (
                  <button 
                    key={index}
                    onClick={() => handleThumbnailClick(index)}
                    className={`shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden border-2 transition-all ${
                      currentIndex === index ? 'border-[#C1A57B] opacity-100' : 'border-transparent opacity-50 hover:opacity-100 hover:border-gray-200'
                    }`}
                  >
                    <img 
                      src={getImageUrl(imgUrl)} 
                      alt={`Thumbnail ${index + 1}`} 
                      className="w-full h-full object-contain bg-[#F7F7F7]" 
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:sticky lg:top-40 text-left">
            <div className="mb-2">
              <span className="text-[#C1A57B] font-bold tracking-widest uppercase text-xs">
                {product.subCategory || product.category}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-4">{product.name}</h1>
            
            {/* UPDATED: Darkened the text to neutral-800 to ensure visibility */}
            <p className="text-sm font-bold tracking-widest uppercase text-neutral-800 mb-8">Custom Quotation Available</p>
            
            <div className="h-px bg-gray-100 w-full mb-8" />

            <div className="prose prose-gray mb-10">
              <p className="text-gray-600 text-lg leading-relaxed">
                {product.description}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-12 relative">
              <button 
                onClick={handleInquiryCartAdd}
                className="flex-1 py-4 md:py-5 bg-black text-white rounded-2xl font-bold hover:bg-[#222] transition-all shadow-xl"
              >
                Add to Inquiry List
              </button>
              <button 
                onClick={() => setShowContactOptions(true)}
                className="flex-1 py-4 md:py-5 border-2 border-black text-black rounded-2xl font-bold hover:bg-gray-50 transition-all flex items-center justify-center gap-3"
              >
                <MessageCircle size={20} /> Direct Inquiry
              </button>

              {addedPopup && (
                <div className="absolute -top-14 left-1/2 -translate-x-1/2 bg-neutral-900 text-white text-xs px-4 py-2.5 rounded-xl shadow-lg border border-neutral-800 animate-in fade-in slide-in-from-bottom-2 z-20">
                  Added to inquiry list successfully!
                </div>
              )}
            </div>

            <div className="bg-[#F9F9F9] p-6 md:p-8 rounded-3xl">
              <h3 className="font-bold text-xl mb-6">Product Highlights</h3>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#C1A57B] shrink-0" /> 
                  <span>Commercial-grade durability</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#C1A57B] shrink-0" /> 
                  <span>Ergonomically tested design</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#C1A57B] shrink-0" /> 
                  <span>Part of the AARTEMA signature collection</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CONTACT OPTIONS MODAL */}
      {showContactOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative animate-in fade-in zoom-in duration-200 text-left">
            <button onClick={() => setShowContactOptions(false)} className="absolute top-4 right-4 text-gray-400 hover:text-black">
              <X size={20} />
            </button>
            <h3 className="text-2xl font-bold mb-2 text-[#1a1a1a]">Send Inquiry</h3>
            <p className="text-gray-500 text-sm mb-6">Choose how you'd like to reach out to us.</p>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                <h4 className="font-bold text-sm mb-1 flex items-center gap-2 text-[#1a1a1a]"><MessageCircle size={16}/> WhatsApp</h4>
                <p className="text-xs text-gray-500 mb-4">Try sending to both numbers for a faster reply.</p>
                <div className="flex gap-3">
                  <button onClick={() => handleWhatsAppInquiry('9289940000')} className="flex-1 bg-[#25D366] text-white py-3 rounded-xl font-bold text-sm hover:bg-[#20BD5A] transition shadow-sm">
                    Number 1
                  </button>
                  <button onClick={() => handleWhatsAppInquiry('9350056070')} className="flex-1 bg-[#25D366] text-white py-3 rounded-xl font-bold text-sm hover:bg-[#20BD5A] transition shadow-sm">
                    Number 2
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                <h4 className="font-bold text-sm mb-3 flex items-center gap-2 text-[#1a1a1a]"><Mail size={16}/> Email</h4>
                <button onClick={handleEmailInquiry} className="w-full bg-black text-white py-3 rounded-xl font-bold text-sm hover:bg-neutral-800 transition shadow-sm">
                  Send via Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
